import React from 'react';
import { motion } from 'framer-motion';

const TeacherSlide04Mastery: React.FC = () => {
  const pillars = [
    { name: 'Numbers & Algebra', modules: 247, color: '#0ea5e9' },
    { name: 'Measurement & Space', modules: 157, color: '#10b981' },
    { name: 'Statistics', modules: 88, color: '#f59e0b' },
    { name: 'Calculus', modules: 16, color: '#8b5cf6' },
    { name: 'Financial Maths', modules: 8, color: '#ec4899' },
  ];

  const tiers = [
    { name: 'Bronze', color: '#cd7f32', desc: 'Initial mastery, daily review' },
    { name: 'Silver', color: '#c0c0c0', desc: 'Strong retention, weekly review' },
    { name: 'Gold', color: '#ffd700', desc: 'Deep understanding, monthly review' },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        The Mastery System
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-[#94a3b8] mb-10 text-center"
      >
        Track individual competency against every curriculum node with spaced repetition
      </motion.p>

      <div className="flex gap-12 w-full max-w-6xl">
        {/* Left: Curriculum Structure */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex-1"
        >
          <h3 className="text-2xl font-semibold text-white mb-6">NSW Maths K-12 Curriculum</h3>

          <div className="space-y-3 mb-6">
            {pillars.map((pillar) => (
              <div key={pillar.name} className="flex items-center gap-4">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ background: pillar.color }}
                />
                <span className="text-lg text-white flex-1">{pillar.name}</span>
                <span
                  className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{ background: `${pillar.color}20`, color: pillar.color }}
                >
                  {pillar.modules} modules
                </span>
              </div>
            ))}
          </div>

          <div
            className="p-5 rounded-xl text-center"
            style={{
              background: 'rgba(14, 165, 233, 0.15)',
              border: '2px solid rgba(14, 165, 233, 0.4)',
            }}
          >
            <div className="text-5xl font-bold text-[#0ea5e9]">680</div>
            <div className="text-lg text-[#94a3b8]">Total Trackable Modules</div>
            <div className="text-sm text-[#64748b] mt-1">516 Maths + 164 English</div>
          </div>
        </motion.div>

        {/* Right: Mastery Tiers */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="flex-1"
        >
          <h3 className="text-2xl font-semibold text-white mb-6">Three Levels of Mastery</h3>

          <div className="space-y-4 mb-6">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className="flex items-center gap-4 p-4 rounded-xl"
                style={{
                  background: `${tier.color}15`,
                  border: `2px solid ${tier.color}`,
                }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                  style={{ background: `${tier.color}30` }}
                >
                  {tier.name === 'Gold' ? '🥇' : tier.name === 'Silver' ? '🥈' : '🥉'}
                </div>
                <div>
                  <div className="text-xl font-semibold" style={{ color: tier.color }}>
                    {tier.name} Mastery
                  </div>
                  <div className="text-sm text-[#94a3b8]">{tier.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="p-4 rounded-xl"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <p className="text-[#94a3b8] text-sm leading-relaxed">
              <span className="text-white font-medium">Spaced repetition:</span> Higher mastery = less frequent review.
              The system automatically schedules reviews based on the Ebbinghaus forgetting curve to prevent decay.
            </p>
          </div>
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="mt-8 text-lg text-[#64748b] text-center"
      >
        Can be applied to any subject. We've also mapped English — and can adapt to any NSW subject curriculum.
      </motion.p>
    </div>
  );
};

export default TeacherSlide04Mastery;
